import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
	INTERACTION_TYPE,
	ITEM_TYPE,
	SCOPE_TYPE,
	SUBSCOPE_TYPE,
} from 'app/interfaces/actiontracker.interface';
import { ActionTrackerService } from 'app/services/actiontracker.service';
import { ZkuService } from 'app/services/zku.service';
import { DateTime } from 'luxon';
import {
	PromptuaryBreadcrumb,
	PromptuaryErrors,
	SentryPromptuaryErrorsMessages,
} from 'modules/promptuary/interfaces/promptuary.interface';
import { debounceTime } from 'rxjs/operators';
import { IAppointment } from 'shared/interfaces/appointment.interface';
import { AppointmentService } from 'shared/services/appointment/appointment.service';
import { PromptuaryService } from 'shared/services/promptuary/promptuary.service';

import { PromptuarySidebarService } from './../../services/sidebar/promptuary-sidebar.service';
import { UnifiedPromptuaryService } from '../promptuary-form/unified-promptuary.service';

@Component({
	selector: 'promptuary-sidebar',
	templateUrl: './promptuary-sidebar.component.html',
	styleUrls: ['./promptuary-sidebar.component.scss'],
})
export class PromptuarySidebarComponent implements OnInit {
	public isSidebarPromptuaryOpen = false;
	public promptuaryType: 'default' | 'crm' = 'default';
	public psychologist;
	public psychiatrist;
	public psychotherapist;
	public isAssistanceJourney;
	public appointmentId = null;
	public contactId = null;
	public promptuary;
	public user;
	public appointment: IAppointment;
	public formatedDate = '';
	public from;
	public mainType = '';
	constructor(
		private promptuarySidebarService: PromptuarySidebarService,
		private appointmentService: AppointmentService,
		private zkuService: ZkuService,
		private actionTrackerService: ActionTrackerService,
		private promptuaryService: PromptuaryService,
		private router: Router,
		public unifiedPromptuary: UnifiedPromptuaryService
	) {}

	ngOnInit(): void {
		this.promptuarySidebarService.isOpenObservable
			.pipe(debounceTime(500))
			.subscribe((isOpen) => {
				if (isOpen) {
					this.isSidebarPromptuaryOpen = true;
					this.unifiedPromptuary.setBreadcrumb(
						PromptuaryBreadcrumb.SIDEBAR_OPEN
					);

					this.getSidebarData();
					this.getAppointmentData();
				} else {
					this.isSidebarPromptuaryOpen = false;
					this.unifiedPromptuary.setBreadcrumb(
						PromptuaryBreadcrumb.SIDEBAR_CLOSE
					);
				}
			});
	}

	getSidebarData() {
		this.appointmentId = this.promptuarySidebarService.state.appointmentId;
		this.contactId = this.promptuarySidebarService.state.contactId;
		this.from = this.promptuarySidebarService.state.from;
		this.mainType = this.promptuarySidebarService.state.mainType;
		this.psychiatrist = this.mainType === 'psychiatrist';
		this.psychologist = this.mainType === 'psychologist';
	}

	getAppointmentData() {
		this.appointmentService
			.getData(this.appointmentId)
			.toPromise()
			.then((appointment: IAppointment) => {
				this.appointment = appointment;
				this.formatedDate = this.capitalizeFirstLetter(
					DateTime.fromISO(this.appointment.day, {
						zone: this.appointment?.timeZone?.name,
					})

						.setLocale('pt-BR')
						.toFormat(`cccc, dd 'de' LLLL '|' T`)
				);
				this.isAssistanceJourney = Boolean(
					appointment?.subscription?.coordinatedCareType
				);
				this.psychotherapist = !this.psychiatrist && this.isAssistanceJourney;
				this.getPromptuary(this.appointmentId, this.contactId);
			})
			.catch(() => {
				this.unifiedPromptuary.capturePromptuaryError(
					PromptuaryErrors.GET_APPOINTMENT_ERROR,
					SentryPromptuaryErrorsMessages.GET_APPOINTMENT_ERROR
				);
				this.close();
			});
	}

	capitalizeFirstLetter(string) {
		return string.charAt(0).toUpperCase() + string.slice(1);
	}

	close() {
		this.promptuarySidebarService.toggleSidebar(false);
		this.unifiedPromptuary.resetPromptuary();
	}

	getPromptuary(appointmentId: string, clientId: string): void {
		this.unifiedPromptuary.resetPromptuary();
		const professionalId = this.zkuService.getZku().id;
		this.promptuaryType = this.psychiatrist ? 'crm' : 'default';
		this.promptuaryService.getData(appointmentId, clientId).subscribe(
			(result) => {
				this.promptuary = result;
				if (this.promptuary.isRegistered) {
					this.router.navigateByUrl(`/promptuary/${appointmentId}/${clientId}`);
					this.close();
				}
				this.unifiedPromptuary.isLoading = false;
				this.unifiedPromptuary.isPromptuaryCreated = true;
				this.trackPromptuarySeen(appointmentId, clientId, professionalId);
			},
			({ error }) => {
				this.promptuary = null;
				if (error?.statusCode === 404) {
					this.unifiedPromptuary.setBreadcrumb(
						PromptuaryBreadcrumb.NOT_FOUND_ERROR
					);
					this.unifiedPromptuary.createPromptuary(clientId, appointmentId);
					return;
				}
				this.unifiedPromptuary.capturePromptuaryError(
					PromptuaryErrors.GET_PROMPTUARY_GENERIC_ERROR,
					SentryPromptuaryErrorsMessages.GET_PROMPTUARY_GENERIC_ERROR
				);
				this.close();
			}
		);
	}

	trackPromptuarySeen(
		appointmentId: string,
		clientId: string,
		professionalId: string
	) {
		this.actionTrackerService.eventTracker(
			SCOPE_TYPE.Promptuary,
			SUBSCOPE_TYPE.Session,
			ITEM_TYPE.Status,
			INTERACTION_TYPE.Seen,
			{
				sessionId: appointmentId,
				contactId: clientId,
				professionalId: professionalId,
				registered: this.promptuary.isRegistered,
			}
		);
	}
}
